// ============================================================
// 📁 آدرس این فایل در پروژه:
//    src/components/Layout.tsx
// 
// این فایل را در همان مسیر بالا کپی کنید (روی فایل قبلی بنویسید)
// ============================================================

import { useState, useEffect, createContext, useContext } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  Home, Crown, DollarSign, Users, UserCheck, ShoppingCart,
  Warehouse, Truck, Shield, BarChart2, MessageSquare,
  Database, LogOut, Settings, Menu, X, Moon, Sun, Bell, Search
} from "lucide-react";
import { useApp } from "../context/AppContext";
import { chatAPI } from "../services/api";
import jalaliMoment from "jalali-moment";

export const ThemeContext = createContext({ dark: true, setDark: (_: boolean) => {} });
export const useTheme = () => useContext(ThemeContext);

export const DARK = {
  bg: "#070714", bg2: "#0e0e2a", sidebar: "#09091f",
  border: "rgba(255,255,255,0.06)", topbar: "rgba(7,7,20,0.92)",
  card: "rgba(255,255,255,0.03)", cardBorder: "rgba(255,255,255,0.07)",
  text: "#f1f5f9", subtext: "#64748b", input: "rgba(255,255,255,0.05)",
  chartGrid: "rgba(255,255,255,0.04)", tooltip: "#0f0f2e",
};

export const LIGHT = {
  bg: "#f8faff", bg2: "#eef2ff", sidebar: "#ffffff",
  border: "rgba(0,0,0,0.07)", topbar: "rgba(255,255,255,0.92)",
  card: "rgba(255,255,255,0.95)", cardBorder: "rgba(0,0,0,0.07)",
  text: "#0f172a", subtext: "#94a3b8", input: "rgba(0,0,0,0.04)",
  chartGrid: "rgba(0,0,0,0.05)", tooltip: "#ffffff",
};

const menuItems = [
  { icon: Home, label: "صفحه اصلی", path: "/dashboard" },
  { icon: Crown, label: "ریاست", path: "/president" },
  { icon: DollarSign, label: "مدیریت مالی", path: "/finance" },
  { icon: Users, label: "منابع انسانی", path: "/hr" },
  { icon: UserCheck, label: "مدیریت مشتریان", path: "/customers" },
  { icon: ShoppingCart, label: "مدیریت فروشندگان", path: "/vendors" },
  { icon: Warehouse, label: "مدیریت گدام", path: "/warehouse" },
  { icon: Truck, label: "پیگیری اجناس", path: "/tracking" },
  { icon: Shield, label: "مدیریت کاربران", path: "/users" },
  { icon: BarChart2, label: "گزارشات", path: "/reports" },
  { icon: MessageSquare, label: "گفتگو", path: "/chat" },
  { icon: Database, label: "بکاپ", path: "/backup" },
  { icon: LogOut, label: "ورود/خروج", path: "/auth" },
  { icon: Settings, label: "تنظیمات سیستم", path: "/settings" },
];

const JALALI_MONTHS = ['حمل','ثور','جوزا','سرطان','اسد','سنبله','میزان','عقرب','قوس','جدی','دلو','حوت'];

const toJalali = (_date: Date) => {
  const m = jalaliMoment();
  const jy  = parseInt(m.format("jYYYY"));
  const jm  = parseInt(m.format("jMM"));
  const jd  = parseInt(m.format("jDD"));
  return { day: jd, month: JALALI_MONTHS[jm - 1], year: jy };
};

export default function Layout() {
  const [dark, setDarkRaw] = useState(() => {
    const saved = localStorage.getItem('theme_mode');
    if (saved === 'dark') return true;
    if (saved === 'light') return false;
    return false;  // پیش‌فرض: حالت روز
  });
  const setDark = (v: boolean) => {
    setDarkRaw(v);
    try { localStorage.setItem('theme_mode', v ? 'dark' : 'light'); } catch {}
  };
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [hoveredMenu, setHoveredMenu] = useState<string|null>(null);
  const [tooltipPos, setTooltipPos] = useState({ top: 0 });
  const [now, setNow] = useState(new Date());
  const [activeAlarm, setActiveAlarm] = useState<any>(null);
  const [alarms, setAlarms] = useState<any[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const T = dark ? DARK : LIGHT;
  const { company, currentUser, logout } = useApp();

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Load alarms on mount and every 5 minutes
  useEffect(() => {
    const loadAlarms = async () => {
      try {
        const r = await chatAPI.getAlarms();
        if (r.success) setAlarms(r.alarms || []);
      } catch {}
    };
    loadAlarms();
    const t = setInterval(loadAlarms, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  // Check alarms every 30 seconds
  useEffect(() => {
    const checkAlarms = () => {
      const m = jalaliMoment();
      const jy = parseInt(m.format("jYYYY"));
      const jm2 = parseInt(m.format("jMM"));
      const jd = parseInt(m.format("jDD"));
      const now2 = new Date();
      const dateStr = `${jy}/${String(jm2).padStart(2,"0")}/${String(jd).padStart(2,"0")}`;
      const timeStr = `${String(now2.getHours()).padStart(2,"0")}:${String(now2.getMinutes()).padStart(2,"0")}`;
      const alarm = alarms.find((a:any) => a.date === dateStr && a.time === timeStr && !a.is_done);
      if (alarm && (!activeAlarm || activeAlarm.id !== alarm.id)) {
        setActiveAlarm(alarm);
        try {
          const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
          const playBeep = (freq: number, startTime: number) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.frequency.value = freq;
            gain.gain.setValueAtTime(0.4, startTime);
            gain.gain.exponentialRampToValueAtTime(0.01, startTime + 0.3);
            osc.start(startTime);
            osc.stop(startTime + 0.3);
          };
          playBeep(880, ctx.currentTime);
          playBeep(660, ctx.currentTime + 0.35);
          playBeep(880, ctx.currentTime + 0.7);
          playBeep(1100, ctx.currentTime + 1.05);
        } catch {}
      }
    };
    const t = setInterval(checkAlarms, 30000);
    checkAlarms();
    return () => clearInterval(t);
  }, [alarms, activeAlarm]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      if (window.innerWidth < 768) {
        setSidebarOpen(false);
        setMobileMenuOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;

  const j = toJalali(now);
  const miladi = now.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  const timeStr = now.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit" });

  const handleMenuClick = (path: string) => {
    if (path === "/auth") {
      logout();
      navigate("/auth");
    } else {
      navigate(path);
    }
    if (isMobile) setMobileMenuOpen(false);
  };

  const handleMenuHover = (path: string | null, e?: React.MouseEvent) => {
    setHoveredMenu(path);
    if (path && e) {
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      setTooltipPos({ top: rect.top + rect.height / 2 });
    }
  };

  return (
    <ThemeContext.Provider value={{ dark, setDark }}>
      <div dir="rtl" style={{
        display: "flex", minHeight: "100vh",
        fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
        color: T.text, position: "relative", overflow: "hidden",
        background: `linear-gradient(160deg, ${T.bg} 0%, ${T.bg2} 100%)`,
        transition: "all 0.4s ease"
      }}>

        {/* Ambient background */}
        <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0, overflow: "hidden" }}>
          <div style={{ position: "absolute", width: 600, height: 600, borderRadius: "50%", filter: "blur(120px)", background: dark ? "rgba(59,130,246,0.07)" : "rgba(59,130,246,0.05)", top: "-200px", right: "10%" }} />
          <div style={{ position: "absolute", width: 500, height: 500, borderRadius: "50%", filter: "blur(100px)", background: dark ? "rgba(139,92,246,0.06)" : "rgba(139,92,246,0.04)", bottom: "-100px", left: "5%" }} />
          <svg viewBox="0 0 1440 900" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: dark ? 0.4 : 0.2 }} preserveAspectRatio="xMidYMid slice">
            <defs>
              <linearGradient id="wg" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor={dark ? "#1e1b4b" : "#c7d2fe"} stopOpacity="1" />
                <stop offset="100%" stopColor={dark ? "#0f172a" : "#e0e7ff"} stopOpacity="0" />
              </linearGradient>
            </defs>
            <path d="M0,0 L0,520 Q200,420 400,500 Q600,580 800,460 Q1000,340 1200,440 Q1380,520 1440,480 L1440,0 Z" fill="url(#wg)" />
            <path d="M0,0 L0,380 Q300,480 600,360 Q900,240 1200,360 Q1380,430 1440,380 L1440,0 Z" fill={dark ? "rgba(30,27,75,0.3)" : "rgba(199,210,254,0.3)"} />
          </svg>
        </div>

        {/* Global Tooltip */}
        {!sidebarOpen && !isMobile && hoveredMenu && (
          <div style={{
            position: "fixed", right: 80, top: tooltipPos.top,
            transform: "translateY(-50%)",
            background: dark ? "#1e293b" : "#0f172a",
            color: "#f1f5f9", fontSize: 12, fontWeight: 600,
            padding: "7px 14px", borderRadius: 8, whiteSpace: "nowrap",
            zIndex: 999, boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
            border: "1px solid rgba(255,255,255,0.12)", pointerEvents: "none",
          }}>
            {menuItems.find(m => m.path === hoveredMenu)?.label}
            <div style={{
              position: "absolute", right: -6, top: "50%", transform: "translateY(-50%)",
              width: 0, height: 0, borderTop: "6px solid transparent",
              borderBottom: "6px solid transparent",
              borderLeft: `6px solid ${dark ? "#1e293b" : "#0f172a"}`,
            }} />
          </div>
        )}

        {/* Mobile overlay */}
        {isMobile && mobileMenuOpen && (
          <div onClick={() => setMobileMenuOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 25, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }} />
        )}

        {/* SIDEBAR - Desktop */}
        {!isMobile && (
          <div style={{
            position: "fixed", right: 0, top: 0, bottom: 0, zIndex: 20,
            width: sidebarOpen ? 250 : 72,
            background: T.sidebar, borderLeft: `1px solid ${T.border}`,
            display: "flex", flexDirection: "column",
            transition: "width 0.35s cubic-bezier(0.4,0,0.2,1)",
            boxShadow: dark ? "-8px 0 40px rgba(0,0,0,0.5)" : "-4px 0 30px rgba(0,0,0,0.08)",
            overflow: "hidden", backdropFilter: "blur(20px)",
          }}>
            {/* Logo */}
            <div style={{ padding: "22px 16px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 12, minHeight: 76 }}>
              <div style={{ width: 42, height: 42, borderRadius: 14, flexShrink: 0, background: "linear-gradient(135deg,#3b82f6,#8b5cf6)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 16px rgba(59,130,246,0.45)", overflow: "hidden" }}>
                {company.logo ? (
                  <img src={company.logo} alt="logo" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                ) : (
                  <span style={{ fontSize: 20, fontWeight: 900, color: "white" }}>{company.name.charAt(0)}</span>
                )}
              </div>
              {sidebarOpen && (
                <div>
                  <div style={{ fontWeight: 800, fontSize: 15, color: T.text, whiteSpace: "nowrap" }}>{company.name}</div>
                  <div style={{ fontSize: 11, color: T.subtext, marginTop: 2 }}>{company.slogan}</div>
                </div>
              )}
            </div>

            {/* Menu */}
            <div style={{ flex: 1, overflowY: "auto", padding: "10px 8px", scrollbarWidth: "none" }}>
              {menuItems.map(({ icon: Icon, label, path }) => {
                const active = location.pathname === path;
                return (
                  <div key={path}
                    onClick={() => handleMenuClick(path)}
                    onMouseEnter={(e) => handleMenuHover(path, e)}
                    onMouseLeave={() => handleMenuHover(null)}
                    style={{
                      display: "flex", alignItems: "center", gap: 12,
                      padding: sidebarOpen ? "11px 14px" : "11px 0",
                      justifyContent: sidebarOpen ? "flex-start" : "center",
                      borderRadius: 12, cursor: "pointer", marginBottom: 3,
                      background: active ? (dark ? "linear-gradient(135deg,rgba(59,130,246,0.18),rgba(139,92,246,0.12))" : "linear-gradient(135deg,rgba(59,130,246,0.12),rgba(139,92,246,0.08))") : hoveredMenu === path ? (dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)") : "transparent",
                      border: active ? "1px solid rgba(59,130,246,0.25)" : "1px solid transparent",
                      transition: "all 0.2s ease", position: "relative", overflow: "hidden"
                    }}>
                    {active && <div style={{ position: "absolute", right: 0, top: "20%", bottom: "20%", width: 3, borderRadius: "0 3px 3px 0", background: "linear-gradient(180deg,#3b82f6,#8b5cf6)" }} />}
                    <Icon size={17} color={active ? "#60a5fa" : hoveredMenu === path ? "#93c5fd" : T.subtext} style={{ flexShrink: 0 }} />
                    {sidebarOpen && <span style={{ fontSize: 13, fontWeight: active ? 600 : 400, color: active ? "#93c5fd" : T.text, whiteSpace: "nowrap" }}>{label}</span>}
                  </div>
                );
              })}
            </div>

            {/* User */}
            {sidebarOpen && (
              <div style={{ margin: "12px", borderRadius: 14, background: dark ? "rgba(59,130,246,0.08)" : "rgba(59,130,246,0.06)", border: `1px solid ${T.border}`, padding: "12px 14px", display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 34, height: 34, borderRadius: 10, background: "linear-gradient(135deg,#3b82f6,#8b5cf6)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, color: "white", flexShrink: 0, overflow: "hidden" }}>
                  {currentUser?.avatar ? (
                    <img src={currentUser.avatar} alt="avatar" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  ) : (
                    currentUser?.fullName?.charAt(0) || "م"
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: T.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{currentUser?.fullName || "مدیر سیستم"}</div>
                  <div style={{ fontSize: 11, color: T.subtext }}>{currentUser?.role === "admin" ? "ادمین" : currentUser?.role === "manager" ? "مدیر" : "کاربر"}</div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* SIDEBAR - Mobile */}
        {isMobile && (
          <div style={{
            position: "fixed", right: mobileMenuOpen ? 0 : "-280px", top: 0, bottom: 0, zIndex: 30,
            width: 270, background: T.sidebar, borderLeft: `1px solid ${T.border}`,
            display: "flex", flexDirection: "column",
            transition: "right 0.35s cubic-bezier(0.4,0,0.2,1)",
            boxShadow: dark ? "-8px 0 40px rgba(0,0,0,0.5)" : "-4px 0 30px rgba(0,0,0,0.08)",
            overflow: "hidden", backdropFilter: "blur(20px)",
          }}>
            <div style={{ padding: "22px 16px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 12, minHeight: 76 }}>
              <div style={{ width: 42, height: 42, borderRadius: 14, flexShrink: 0, background: "linear-gradient(135deg,#3b82f6,#8b5cf6)", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                {company.logo ? (
                  <img src={company.logo} alt="logo" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                ) : (
                  <span style={{ fontSize: 20, fontWeight: 900, color: "white" }}>{company.name.charAt(0)}</span>
                )}
              </div>
              <div>
                <div style={{ fontWeight: 800, fontSize: 15, color: T.text }}>{company.name}</div>
                <div style={{ fontSize: 11, color: T.subtext, marginTop: 2 }}>{company.slogan}</div>
              </div>
              <button onClick={() => setMobileMenuOpen(false)} style={{ marginRight: "auto", background: "none", border: "none", cursor: "pointer", color: T.text, display: "flex" }}>
                <X size={20} />
              </button>
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: "10px 8px", scrollbarWidth: "none" }}>
              {menuItems.map(({ icon: Icon, label, path }) => {
                const active = location.pathname === path;
                return (
                  <div key={path} onClick={() => handleMenuClick(path)} style={{
                    display: "flex", alignItems: "center", gap: 12, padding: "11px 14px",
                    borderRadius: 12, cursor: "pointer", marginBottom: 3,
                    background: active ? (dark ? "linear-gradient(135deg,rgba(59,130,246,0.18),rgba(139,92,246,0.12))" : "linear-gradient(135deg,rgba(59,130,246,0.12),rgba(139,92,246,0.08))") : "transparent",
                    border: active ? "1px solid rgba(59,130,246,0.25)" : "1px solid transparent",
                    transition: "all 0.2s ease", position: "relative", overflow: "hidden"
                  }}>
                    {active && <div style={{ position: "absolute", right: 0, top: "20%", bottom: "20%", width: 3, borderRadius: "0 3px 3px 0", background: "linear-gradient(180deg,#3b82f6,#8b5cf6)" }} />}
                    <Icon size={17} color={active ? "#60a5fa" : T.subtext} style={{ flexShrink: 0 }} />
                    <span style={{ fontSize: 13, fontWeight: active ? 600 : 400, color: active ? "#93c5fd" : T.text, whiteSpace: "nowrap" }}>{label}</span>
                  </div>
                );
              })}
            </div>
            <div style={{ margin: "12px", borderRadius: 14, background: dark ? "rgba(59,130,246,0.08)" : "rgba(59,130,246,0.06)", border: `1px solid ${T.border}`, padding: "12px 14px", display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 34, height: 34, borderRadius: 10, background: "linear-gradient(135deg,#3b82f6,#8b5cf6)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, color: "white", flexShrink: 0 }}>
                {currentUser?.fullName?.charAt(0) || "م"}
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{currentUser?.fullName || "مدیر سیستم"}</div>
                <div style={{ fontSize: 11, color: T.subtext }}>{currentUser?.role === "admin" ? "ادمین" : "کاربر"}</div>
              </div>
            </div>
          </div>
        )}

        {/* MAIN */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", marginRight: isMobile ? 0 : (sidebarOpen ? 250 : 72), transition: "margin-right 0.35s cubic-bezier(0.4,0,0.2,1)", zIndex: 1, minWidth: 0 }}>

          {/* TOPBAR */}
          <div style={{ position: "sticky", top: 0, zIndex: 15, background: T.topbar, backdropFilter: "blur(24px)", borderBottom: `1px solid ${T.border}`, padding: isMobile ? "0 12px" : "0 24px", height: isMobile ? 60 : 76, display: "flex", alignItems: "center", justifyContent: "space-between", boxShadow: dark ? "0 1px 30px rgba(0,0,0,0.4)" : "0 1px 20px rgba(0,0,0,0.06)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 8 : 14 }}>
              <button onClick={() => isMobile ? setMobileMenuOpen(!mobileMenuOpen) : setSidebarOpen(!sidebarOpen)} style={{ width: 38, height: 38, background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)", border: `1px solid ${T.border}`, borderRadius: 10, cursor: "pointer", color: T.text, display: "flex", alignItems: "center", justifyContent: "center" }}>
                {(!isMobile && sidebarOpen) ? <X size={17} /> : <Menu size={17} />}
              </button>
              {!isMobile && (
                <div style={{ display: "flex", alignItems: "stretch", background: dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)", border: `1px solid ${T.border}`, borderRadius: 12, overflow: "hidden" }}>
                  {[
                    { label: "شمسی", value: `${j.day} ${j.month} ${j.year}`, color: "#60a5fa" },
                    { label: "میلادی", value: isTablet ? miladi.substring(0, 12) : miladi, color: T.text },
                    { label: "ساعت", value: timeStr, color: "#a78bfa" },
                  ].map((item, idx) => (
                    <div key={idx} style={{ padding: isTablet ? "8px 10px" : "8px 14px", textAlign: "center", borderRight: idx < 2 ? `1px solid ${T.border}` : "none" }}>
                      <div style={{ fontSize: 9, color: T.subtext, letterSpacing: "0.5px", marginBottom: 2 }}>{item.label}</div>
                      <div style={{ fontSize: isTablet ? 11 : 12, fontWeight: 700, color: item.color, whiteSpace: "nowrap" }}>{item.value}</div>
                    </div>
                  ))}
                </div>
              )}
              {isMobile && (
                <div style={{ display: "flex", alignItems: "center", gap: 4, background: dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)", border: `1px solid ${T.border}`, borderRadius: 10, padding: "6px 10px" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#60a5fa" }}>{j.day} {j.month}</div>
                  <div style={{ width: 1, height: 14, background: T.border }} />
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#a78bfa" }}>{timeStr}</div>
                </div>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 6 : 10 }}>
              {!isMobile && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, background: T.input, border: `1px solid ${T.border}`, borderRadius: 10, padding: "8px 14px" }}>
                  <Search size={14} color={T.subtext} />
                  <input placeholder="جستجو..." style={{ background: "none", border: "none", outline: "none", color: T.text, fontSize: 13, width: isTablet ? 100 : 130, fontFamily: "inherit", direction: "rtl" }} />
                </div>
              )}
              {isMobile && (
                <button style={{ width: 34, height: 34, background: T.input, border: `1px solid ${T.border}`, borderRadius: 9, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Search size={15} color={T.subtext} />
                </button>
              )}
              <button onClick={() => setDark(!dark)} style={{ width: isMobile ? 34 : 38, height: isMobile ? 34 : 38, background: dark ? "rgba(251,191,36,0.15)" : "rgba(99,102,241,0.1)", border: `1px solid ${T.border}`, borderRadius: 10, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                {dark ? <Sun size={16} color="#fbbf24" /> : <Moon size={16} color="#6366f1" />}
              </button>
              <button style={{ width: isMobile ? 34 : 38, height: isMobile ? 34 : 38, background: T.input, border: `1px solid ${T.border}`, borderRadius: 10, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
                <Bell size={16} color={T.subtext} />
                <span style={{ position: "absolute", top: 7, right: 7, width: 7, height: 7, background: "#ef4444", borderRadius: "50%", boxShadow: "0 0 6px rgba(239,68,68,0.8)" }} />
              </button>
              <div onClick={() => navigate("/settings")} style={{ width: isMobile ? 34 : 38, height: isMobile ? 34 : 38, borderRadius: 10, background: "linear-gradient(135deg,#3b82f6,#8b5cf6)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 800, color: "white", cursor: "pointer", boxShadow: "0 4px 14px rgba(59,130,246,0.4)", overflow: "hidden" }}>
                {currentUser?.avatar ? (
                  <img src={currentUser.avatar} alt="avatar" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                ) : (
                  currentUser?.fullName?.charAt(0) || "م"
                )}
              </div>
            </div>
          </div>

          <div style={{ flex: 1, overflowY: "auto" }}>
            <Outlet />
          </div>
        </div>
      </div>
      {/* Global Alarm Popup */}
      {activeAlarm && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.75)", zIndex:999999, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Vazirmatn','Segoe UI',sans-serif" }}>
          <div style={{ background: dark ? "#1e293b" : "#ffffff", borderRadius:24, padding:40, maxWidth:420, width:"90%", textAlign:"center", boxShadow:"0 24px 64px rgba(0,0,0,0.5)", border:`2px solid ${dark?"rgba(239,68,68,0.4)":"#fecaca"}` }}>
            <div style={{ fontSize:60, marginBottom:16 }}>⏰</div>
            <h2 style={{ color: dark ? "#f1f5f9" : "#0f172a", margin:"0 0 8px", fontSize:22, fontWeight:800 }}>{activeAlarm.title}</h2>
            <p style={{ color:"#94a3b8", fontSize:14, margin:"0 0 8px" }}>{activeAlarm.date} — {activeAlarm.time}</p>
            {activeAlarm.note && <p style={{ color:"#94a3b8", fontSize:13, margin:"0 0 24px" }}>{activeAlarm.note}</p>}
            <div style={{ display:"flex", gap:12, justifyContent:"center" }}>
              <button onClick={()=>{ setActiveAlarm(null); chatAPI.updateAlarm({ id:activeAlarm.id, done:1 }); setAlarms((prev:any[])=>prev.map((a:any)=>a.id===activeAlarm.id?{...a,is_done:1}:a)); }} style={{ padding:"12px 32px", borderRadius:12, border:"none", background:"#6366f1", color:"#fff", fontWeight:700, cursor:"pointer", fontSize:15, fontFamily:"inherit" }}>
                ✓ بستن
              </button>
              <button onClick={()=>{ 
                const snoozeAlarm = {...activeAlarm};
                setActiveAlarm(null);
                setTimeout(()=>setActiveAlarm(snoozeAlarm), 5*60*1000);
              }} style={{ padding:"12px 32px", borderRadius:12, border:`1px solid ${dark?"rgba(255,255,255,0.15)":"#e2e8f0"}`, background:"transparent", color: dark?"#f1f5f9":"#0f172a", fontWeight:700, cursor:"pointer", fontSize:15, fontFamily:"inherit" }}>
                ۵ دقیقه دیگر
              </button>
            </div>
          </div>
        </div>
      )}
    </ThemeContext.Provider>
  );
}